/**
 * Context for providing route name map to the application
 */
import { buildRouteMaps, RouteMaps } from "./routeHelpers";
import { RouteDefinition } from "./types";
import React, { createContext, useMemo } from "react";

/**
 * Context value containing route maps
 */
export interface RouteMapContextValue {
  /** Map of route names to path patterns */
  pathMap: Map<string, string>;
  /** Map of route names to their default parameters */
  defaultParamsMap: Map<string, Record<string, any>>;
}

const defaultContextValue: RouteMapContextValue = {
  defaultParamsMap: new Map(),
  pathMap: new Map(),
};

// Context that holds route maps (path patterns and default params)
export const RouteMapContext =
  createContext<RouteMapContextValue>(defaultContextValue);

interface RouteMapProviderProps {
  children: React.ReactNode;
  routes: RouteDefinition[];
}

/**
 * Provider that builds and exposes the route name map
 */
export const RouteMapProvider: React.FC<RouteMapProviderProps> = ({
  children,
  routes,
}) => {
  const routeMaps = useMemo<RouteMapContextValue>(() => {
    const maps: RouteMaps = buildRouteMaps(routes);

    return {
      defaultParamsMap: maps.defaultParamsMap,
      pathMap: maps.pathMap,
    };
  }, [routes]);

  return (
    <RouteMapContext.Provider value={routeMaps}>
      {children}
    </RouteMapContext.Provider>
  );
};
